import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import BoutonArreter, { useArretAgent } from "@/components/ArretAgent";
import { toast } from "sonner";
import {
  Send, Loader2, Bot, Plus, FileText, Edit3, Terminal, CheckCircle2, AlertCircle,
  Circle, ShieldCheck, ShieldAlert, Unlock, Lock, Globe,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const SUGGESTIONS = [
  "Fais le point sur l'état du serveur : disque, services arrêtés, erreurs récentes.",
  "Quels sites répondent mal en ce moment, et pourquoi ?",
  "Explique-moi les constats de sécurité en cours et par quoi commencer.",
  "Qu'est-ce qui a changé sur le serveur depuis hier ?",
];

export default function AgentPage() {
  const { token } = useAuth();
  const [sante, setSante] = useState(null);
  const [contexte, setContexte] = useState(null);
  const [evenements, setEvenements] = useState([]);
  const [saisie, setSaisie] = useState("");
  const [encours, setEncours] = useState(false);
  const arret = useArretAgent(token, "/api/agent/stop");
  const [sessionId, setSessionId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [ecrire, setEcrire] = useState(false);
  const zoneRef = useRef(null);
  const authRef = useRef({ headers: { Authorization: `Bearer ${token}` } });
  authRef.current = { headers: { Authorization: `Bearer ${token}` } };

  const verifierSante = useCallback(() => {
    axios.get(`${API_URL}/api/claude/health`, authRef.current)
      .then(({ data }) => setSante(data))
      .catch(() => setSante({ ok: false, detail: "Vérification impossible" }));
  }, []);

  const chargerConversations = useCallback(() => {
    axios.get(`${API_URL}/api/agent/conversations`, authRef.current)
      .then(({ data }) => setConversations(data)).catch(() => {});
  }, []);

  useEffect(() => {
    verifierSante();
    chargerConversations();
    axios.get(`${API_URL}/api/agent/contexte`, authRef.current)
      .then(({ data }) => setContexte(data)).catch(() => {});
    const t = setInterval(verifierSante, 60000);
    return () => clearInterval(t);
  }, [verifierSante, chargerConversations]);

  useEffect(() => {
    if (zoneRef.current) zoneRef.current.scrollTop = zoneRef.current.scrollHeight;
  }, [evenements]);

  const ouvrirConversation = async (sid) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/agent/conversations/${sid}`, authRef.current);
      setEvenements((data.messages || []).map((m) =>
        ({ genre: m.role === "user" ? "user" : "assistant", texte: m.text })));
      setSessionId(sid);
    } catch (e) {
      toast.error("Conversation introuvable");
    }
  };

  const nouvelleConversation = () => { setEvenements([]); setSessionId(null); };

  const pousser = (e) => setEvenements((prev) => [...prev, e]);

  const traiter = (evt) => {
    if (evt.type === "done") {
      if (evt.session_id) setSessionId(evt.session_id);
      chargerConversations();
      return;
    }
    if (evt.type === "error") { pousser({ genre: "erreur", texte: evt.message }); return; }
    if (evt.type === "assistant" && evt.message?.content) {
      for (const c of evt.message.content) {
        if (c.type === "text" && c.text) pousser({ genre: "assistant", texte: c.text });
        if (c.type === "tool_use") pousser({ genre: "outil", nom: c.name, entree: c.input });
      }
    }
    if (evt.type === "result") {
      pousser({ genre: "fin",
                meta: `${evt.num_turns || 0} tours · ${((evt.duration_ms || 0) / 1000).toFixed(1)} s` });
      if (evt.session_id) setSessionId(evt.session_id);
    }
  };

  const envoyer = async (prompt) => {
    if (!prompt.trim() || encours) return;
    setEncours(true);
    pousser({ genre: "user", texte: prompt });
    setSaisie("");
    try {
      const res = await fetch(`${API_URL}/api/agent/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        ...(() => { const { signal, run_id } = arret.demarrer();
          return { body: JSON.stringify({ prompt, resume: sessionId, ecrire, run_id }), signal }; })(),
      });
      if (!res.ok) { toast.error("L'agent n'a pas démarré"); setEncours(false); return; }
      const lecteur = res.body.getReader();
      const decodeur = new TextDecoder();
      let tampon = "";
      while (true) {
        const { done, value } = await lecteur.read();
        if (done) break;
        tampon += decodeur.decode(value, { stream: true });
        const morceaux = tampon.split("\n\n");
        tampon = morceaux.pop();
        for (const m of morceaux) {
          if (!m.startsWith("data: ")) continue;
          try { traiter(JSON.parse(m.slice(6))); } catch { /* ligne partielle */ }
        }
      }
    } catch (e) {
      if (arret.estArret(e)) pousser({ genre: "fin", meta: "⏹ Arrêté à ta demande" });
      else toast.error("Connexion interrompue");
    } finally {
      setEncours(false);
      verifierSante();
    }
  };

  const iconeOutil = (nom) => {
    if (nom === "Read" || nom === "Grep" || nom === "Glob") return <FileText className="w-3.5 h-3.5" />;
    if (nom === "Write" || nom === "Edit") return <Edit3 className="w-3.5 h-3.5" />;
    return <Terminal className="w-3.5 h-3.5" />;
  };
  const libelleOutil = (e) =>
    `${e.nom} ${e.entree?.file_path || e.entree?.command || e.entree?.pattern || ""}`.trim();

  const secu = contexte?.securite;

  return (
    <CRMLayout>
      <div className="flex min-h-[calc(100dvh-4rem)] max-w-6xl flex-col p-4 sm:p-6 lg:h-[calc(100vh-4rem)] lg:overflow-hidden lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Bot className="w-6 h-6 text-indigo-600" /> Agent NovaForge
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Claude branché sur tout le serveur (dossier {contexte?.dossier || "/opt"}) :
              exploitation, logs, sites, sécurité. Même compte que le chat des projets.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {secu && (
              <Link to="/dashboard/securite"
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border
                               bg-white hover:border-indigo-300">
                {secu.note >= 90 ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                                 : <ShieldAlert className="w-3.5 h-3.5 text-red-600" />}
                Sécurité {secu.note}/100
              </Link>
            )}
            <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border ${
              sante?.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : "bg-red-50 border-red-200 text-red-700"}`}>
              {sante?.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              {sante?.ok ? "Claude connecté" : (sante?.detail || "Claude indisponible")}
            </span>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
          {/* Conversations */}
          <div className="flex w-full shrink-0 flex-col lg:w-56">
            <button onClick={nouvelleConversation}
                    className="w-full mb-2 inline-flex items-center justify-center gap-1.5 bg-indigo-600
                               hover:bg-indigo-700 text-white rounded-lg px-3 py-2 text-sm font-semibold">
              <Plus className="w-4 h-4" /> Nouvelle discussion
            </button>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 mb-1.5">
              Historique
            </div>
            <div className="max-h-36 flex-1 space-y-1 overflow-y-auto lg:max-h-none">
              {conversations.length === 0 && (
                <div className="text-xs text-slate-400 px-1 py-3">Aucune discussion encore.</div>
              )}
              {conversations.map((c) => (
                <button key={c.session_id} onClick={() => ouvrirConversation(c.session_id)}
                        className={`w-full text-left rounded-lg px-2.5 py-2 hover:bg-slate-100 ${
                          sessionId === c.session_id ? "bg-indigo-50 border border-indigo-200"
                                                     : "border border-transparent"}`}>
                  <div className="text-xs font-medium text-slate-700 line-clamp-2">{c.title}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {c.count} msg · {c.updated_at ? new Date(c.updated_at).toLocaleDateString("fr-FR") : ""}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Conversation en cours */}
          <div className="flex min-h-[520px] min-w-0 flex-1 flex-col rounded-xl border border-slate-200 bg-white lg:min-h-0">
            <div ref={zoneRef} className="flex-1 overflow-y-auto p-5 space-y-3">
              {evenements.length === 0 && (
                <div className="text-center py-10">
                  <Bot className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm mb-4">
                    Posez une question sur le serveur, les sites, un déploiement, un incident.
                  </p>
                  <div className="grid gap-2 max-w-xl mx-auto">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => envoyer(s)}
                              className="text-left text-sm text-slate-700 bg-slate-50 hover:bg-indigo-50
                                         border border-slate-200 hover:border-indigo-200 rounded-lg px-3 py-2">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {evenements.map((e, i) => {
                if (e.genre === "user") {
                  return (
                    <div key={i} className="flex justify-end">
                      <div className="bg-indigo-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5
                                      max-w-[85%] whitespace-pre-wrap text-sm">{e.texte}</div>
                    </div>
                  );
                }
                if (e.genre === "assistant") {
                  return (
                    <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl rounded-bl-sm
                                            px-4 py-3 max-w-[95%] whitespace-pre-wrap text-sm text-slate-800">
                      {e.texte}
                    </div>
                  );
                }
                if (e.genre === "outil") {
                  return (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-500 font-mono pl-1">
                      {iconeOutil(e.nom)}
                      <span className="truncate">{libelleOutil(e)}</span>
                    </div>
                  );
                }
                if (e.genre === "erreur") {
                  return (
                    <div key={i} className="text-sm text-red-700 bg-red-50 border border-red-200
                                            rounded-lg px-3 py-2">{e.texte}</div>
                  );
                }
                return (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-400 pl-1">
                    <Circle className="w-2.5 h-2.5" /> {e.meta}
                  </div>
                );
              })}
              {encours && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> L'agent travaille…
                </div>
              )}
            </div>

            {/* Composeur */}
            <div className="border-t border-slate-200 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Switch checked={ecrire} onCheckedChange={setEcrire} id="mode-ecriture" />
                <label htmlFor="mode-ecriture"
                       className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer">
                  {ecrire ? <Unlock className="w-3.5 h-3.5 text-orange-600" />
                          : <Lock className="w-3.5 h-3.5 text-slate-400" />}
                  {ecrire
                    ? "Modifications autorisées — l'agent peut écrire des fichiers et lancer des commandes"
                    : "Lecture seule — l'agent observe, il ne modifie rien"}
                </label>
              </div>
              <div className="flex gap-2">
                <textarea
                  value={saisie}
                  onChange={(e) => setSaisie(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); envoyer(saisie); }
                  }}
                  rows={2}
                  placeholder="Votre question… (Entrée pour envoyer, Maj+Entrée pour un retour à la ligne)"
                  className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
                {encours ? (
                  <BoutonArreter onClick={arret.arreter} className="self-end" />
                ) : (
                  <Button onClick={() => envoyer(saisie)} disabled={!saisie.trim()} className="self-end">
                    <Send className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CRMLayout>
  );
}
